/**
 * CCTV 등록·구역 편집용 스트림 1프레임 JPEG.
 * 1) 현장 PC 로컬 DataGateway (127.0.0.1:28765) — VLC와 같은 대역
 * 2) 중앙 /api/cctv/stream-preview
 */
import axios from 'axios';
import { API_ORIGIN } from '../config';
import { getBearerTokenForApi } from './poolSiteAuth';

export const LOCAL_PREVIEW_ORIGIN = 'http://127.0.0.1:28765';

export function isRtspUrl(url: string): boolean {
  const lower = (url || '').trim().toLowerCase();
  return lower.startsWith('rtsp://') || lower.startsWith('rtsps://');
}

export function isHttpUrl(url: string): boolean {
  const lower = (url || '').trim().toLowerCase();
  return lower.startsWith('http://') || lower.startsWith('https://');
}

export async function isLocalPreviewUp(): Promise<boolean> {
  try {
    const ctrl = new AbortController();
    const t = window.setTimeout(() => ctrl.abort(), 800);
    const res = await fetch(`${LOCAL_PREVIEW_ORIGIN}/api/status`, {
      signal: ctrl.signal,
    });
    window.clearTimeout(t);
    return res.ok;
  } catch {
    return false;
  }
}

async function fetchLocalStreamJpeg(streamUrl: string): Promise<Blob> {
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), 20000);
  try {
    const res = await fetch(`${LOCAL_PREVIEW_ORIGIN}/api/stream-preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: streamUrl, rtsp_url: streamUrl }),
      signal: ctrl.signal,
    });
    if (res.ok) {
      const ctype = res.headers.get('Content-Type') || '';
      if (ctype.includes('image/') || ctype.includes('octet-stream')) {
        return await res.blob();
      }
    }
    let detail = `로컬 미리보기 HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j?.message) detail = String(j.message);
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('로컬 미리보기 시간 초과 (20초)');
    }
    throw err;
  } finally {
    window.clearTimeout(t);
  }
}

async function fetchCentralStreamJpeg(streamUrl: string): Promise<Blob> {
  const token = getBearerTokenForApi();
  const endpoints = [
    `/api/cctv/stream-preview`,
    `${API_ORIGIN}/api/cctv/stream-preview`,
    `/api/cctv/rtsp-preview`,
    `${API_ORIGIN}/api/cctv/rtsp-preview`,
  ];
  let lastDetail = '미리보기 실패';
  const body = { url: streamUrl, rtsp_url: streamUrl };

  for (const url of endpoints) {
    try {
      const res = await axios.post(url, body, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 25000,
        validateStatus: () => true,
      });
      if (res.status >= 200 && res.status < 300 && res.data instanceof Blob) {
        const ctype = String(res.headers['content-type'] || '');
        if (ctype.includes('application/json') || ctype.includes('text/')) {
          const text = await res.data.text();
          try {
            const parsed = JSON.parse(text);
            lastDetail = String(parsed?.detail || parsed?.message || lastDetail);
          } catch {
            lastDetail = text.slice(0, 200) || lastDetail;
          }
          continue;
        }
        if (
          ctype.includes('image/') ||
          res.data.type.startsWith('image/') ||
          res.data.size > 100
        ) {
          return res.data;
        }
      }
      try {
        const text = await (res.data as Blob).text();
        const parsed = JSON.parse(text);
        lastDetail = String(parsed?.detail || parsed?.message || `HTTP ${res.status}`);
      } catch {
        lastDetail = `HTTP ${res.status}`;
      }
      if (res.status === 401 || res.status === 404) continue;
      break;
    } catch (err) {
      lastDetail = err instanceof Error ? err.message : String(err);
    }
  }
  throw new Error(lastDetail);
}

/** 로컬 우선 → 실패 시 중앙 */
export async function fetchStreamJpegBlob(streamUrl: string): Promise<Blob> {
  const trimmed = (streamUrl || '').trim();
  if (!trimmed) throw new Error('스트림 URL 없음');

  let localErr = '';
  if (await isLocalPreviewUp()) {
    try {
      return await fetchLocalStreamJpeg(trimmed);
    } catch (err) {
      localErr = err instanceof Error ? err.message : String(err);
    }
  }

  try {
    return await fetchCentralStreamJpeg(trimmed);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    const hint =
      ' (현장에서는 이 PC에서 DataGateway를 켠 뒤 다시 시도 — 전송 시작 불필요)';
    throw new Error(localErr ? `로컬: ${localErr} / 중앙: ${msg}${hint}` : `${msg}${hint}`);
  }
}
